import { useState } from "react";
import {
  Alert, Box, Chip, MenuItem, Paper, Stack, TextField,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { predictiveReorderApi, warehousesApi, type PredictiveReorderRow } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

/** Tone for the VED chip — vital reads as the most urgent. */
const VED_TONE = { Vital: "error", Essential: "warning", Desirable: "default" } as const;
/** Tone for the FSN chip; NonMoving is deliberately muted (dead stock, not an alarm). */
const FSN_TONE = { Fast: "info", Slow: "default", NonMoving: "default" } as const;
const FSN_LABEL = { Fast: "Fast", Slow: "Slow", NonMoving: "Non-moving" } as const;

/**
 * S14 — Predictive reorder. ADC (avg daily consumption from the ledger) drives a
 * dynamic reorder point = ADC×LeadTime + effective SafetyStock, with days-of-cover so planners
 * can act before a stockout rather than at the static ReorderLevel line.
 *
 * The safety-stock term is scaled by criticality: VED (stored on the item) × FSN (derived from
 * consumption in the window). Vital and fast-moving stock therefore carries a wider buffer and trips
 * its reorder earlier than desirable or non-moving stock on the same demand.
 */
export function PredictiveReorderPage() {
  const [warehouseId, setWarehouseId] = useState("");
  const [windowDays, setWindowDays] = useState(90);
  const [onlyBelowRop, setOnlyBelowRop] = useState(true);

  const warehouses = useQuery({
    queryKey: ["warehouses-all"], queryFn: () => warehousesApi.list({ pageSize: 200 }),
    staleTime: 5 * 60_000,
  });

  const query = useQuery({
    queryKey: ["predictive-reorder", warehouseId, windowDays, onlyBelowRop],
    queryFn: () => predictiveReorderApi.list(warehouseId, windowDays, onlyBelowRop),
    enabled: !!warehouseId,
  });

  const columns: GridColDef<PredictiveReorderRow>[] = [
    { field: "sku", headerName: "SKU", width: 120 },
    { field: "name", headerName: "Item", flex: 1, minWidth: 200 },
    { field: "availableQty", headerName: "Available", width: 100, type: "number",
      description: "On the shelf now, less active reservations — the same figure the reorder queue shows" },
    { field: "onOrderQty", headerName: "On order", width: 100, type: "number",
      description: "Already on a live purchase order for this warehouse and not yet received",
      valueFormatter: (v) => Number(v) > 0 ? String(v) : "—" },
    { field: "inventoryPositionQty", headerName: "Position", width: 100, type: "number",
      description: "Available + on order — what the predicted ROP is compared against" },
    { field: "avgDailyConsumption", headerName: "ADC/day", width: 100, type: "number",
      valueFormatter: (v) => Number(v).toFixed(2) },
    { field: "leadTimeDays", headerName: "Lead (d)", width: 90, type: "number" },
    { field: "vedClass", headerName: "VED", width: 105,
      renderCell: (p) => p.row.vedClass
        ? <Chip size="small" variant="outlined" color={VED_TONE[p.row.vedClass]} label={p.row.vedClass} />
        : <>—</> },
    { field: "fsnClass", headerName: "FSN", width: 115,
      renderCell: (p) => (
        <Chip size="small" variant="outlined" color={FSN_TONE[p.row.fsnClass]} label={FSN_LABEL[p.row.fsnClass]} />
      ) },
    { field: "baseSafetyStock", headerName: "Safety (base)", width: 120, type: "number" },
    { field: "effectiveSafetyStock", headerName: "Safety (buffered)", width: 150, type: "number",
      description: "Base safety stock × VED factor × FSN factor — the value used in the ROP.",
      renderCell: (p) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <span>{Number(p.row.effectiveSafetyStock).toFixed(1)}</span>
          {p.row.bufferFactor !== 1 && (
            <Chip size="small" variant="outlined" color="info" label={`×${Number(p.row.bufferFactor).toFixed(2)}`} />
          )}
        </Stack>
      ) },
    { field: "predictedReorderPoint", headerName: "Pred. ROP", width: 110, type: "number",
      valueFormatter: (v) => Number(v).toFixed(1) },
    { field: "daysOfCover", headerName: "Cover (d)", width: 110,
      renderCell: (p) => p.value == null
        ? <Chip size="small" label="∞" />
        : <Chip size="small" color={Number(p.value) < 7 ? "error" : Number(p.value) < 21 ? "warning" : "default"}
            label={Number(p.value).toFixed(1)} /> },
    { field: "suggestedQty", headerName: "Suggest", width: 100, type: "number" },
    { field: "shouldReorder", headerName: "Reorder?", width: 100,
      renderCell: (p) => p.value
        ? <Chip size="small" color="error" label="YES" />
        : <Chip size="small" label="ok" /> },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Inventory"]} title="Predictive reorder" />
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Warehouse *" size="small" sx={{ minWidth: 280 }}
            value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <MenuItem value="">— Pick warehouse —</MenuItem>
            {warehouses.data?.items.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Window" size="small" sx={{ minWidth: 140 }}
            value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}>
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={60}>60 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
            <MenuItem value={180}>180 days</MenuItem>
          </TextField>
          <TextField select label="Filter" size="small" sx={{ minWidth: 180 }}
            value={onlyBelowRop ? "below" : "all"}
            onChange={(e) => setOnlyBelowRop(e.target.value === "below")}>
            <MenuItem value="below">Below ROP only</MenuItem>
            <MenuItem value="all">All consuming items</MenuItem>
          </TextField>
        </Stack>
        {!warehouseId && <Alert severity="info">Pick a warehouse to compute predictions.</Alert>}
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {warehouseId && (
          <Box sx={{ height: 600 }}>
            <DataGrid
              rows={query.data ?? []} columns={columns} loading={query.isLoading}
              disableRowSelectionOnClick getRowId={(row) => row.itemId}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
